// ── Participants tab — the cohort roster + Wave-5 add/import affordances ─────
// Reads GET /cohorts/:id/participants via the EXISTING typed service
// (participants.listForCohort) wrapped in useHonestQuery -> CohortTabShell.

import { Alert, Badge, Button, CollectionPagination, CollectionToolbar, DataTableV2, useToast } from "@vjal/ui";
import { useMemo, useState } from "react";

import { useHonestQuery } from "../../../components/honest-state";
import { getCoachingServices } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import {
  useParticipantCreateMutation,
  useCohortLifecycleMutations,
} from "../../../hooks/useCoachingQueries";
import { evaluateActionCapability } from "../../../domain/capabilities";
import { asLiveApiId } from "../../../hooks/useLiveProductData";
import type {
  CoachingCohortDto,
  CoachingOperationResult,
  CoachingParticipantDto,
  ListResponse,
} from "../../../services/coaching-dtos";
import type { CoachingRole } from "../../../domain/roles";
import { CohortTabShell } from "./cohort-tab-shell";
import { ParticipantFormModal, type ParticipantFormValues } from "../../participants/participant-form-modal";
import { BulkImportModal } from "../../participants/bulk-import/bulk-import-modal";
import { resolveCoachCutoff } from "../../participants/bulk-import/coach-cutoff";
import { useCohortParticipantsCollection } from "./cohort-participants-collection";

const services = getCoachingServices();

export interface CohortParticipantsTabProps {
  cohortId: string;
  role: CoachingRole;
  cohort: CoachingCohortDto;
}

export function CohortParticipantsTab({ cohortId, role, cohort }: CohortParticipantsTabProps) {
  const toast = useToast();
  const create = useParticipantCreateMutation();
  const lifecycle = useCohortLifecycleMutations();

  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const query = useHonestQuery<ListResponse<CoachingParticipantDto>>(
    coachingQueryKeys.list("participants.listForCohort", { cohort_id: cohortId }),
    () => services.participants.listForCohort(cohortId),
  );
  const items = query.data?.items;
  const allRows = useMemo(() => items ?? [], [items]);
  const collection = useCohortParticipantsCollection(allRows);

  const writeAllowed = evaluateActionCapability(role, "participants:create").allowed;
  const importAllowed = evaluateActionCapability(role, "participants:bulk-import").allowed;
  const cutoff = useMemo(() => resolveCoachCutoff(role, cohort.start_date ?? null), [role, cohort.start_date]);
  const cutoffClosed = cutoff.state === "closed";
  const canAdd = writeAllowed && cutoff.allowAddImport;
  const canImport = importAllowed && cutoff.allowAddImport;

  const existingEmails = useMemo(
    () => allRows.map((row) => row.email?.trim().toLowerCase()).filter((email): email is string => Boolean(email)),
    [allRows],
  );

  function submitAdd(values: ParticipantFormValues): Promise<CoachingParticipantDto> {
    return create
      .mutateAsync({
        email: values.email,
        ...(values.name ? { name: values.name } : {}),
        cohortId: asLiveApiId(cohortId),
      })
      .then((participant) => {
        setAddOpen(false);
        create.reset();
        void query.retry();
        toast.success(`Participant ${participant.name ?? values.email} added`);
        return participant;
      });
  }

  async function submitImport(importRows: Array<Record<string, unknown>>): Promise<CoachingOperationResult> {
    const operation = await lifecycle.bulkImport.mutateAsync({ cohortId, body: { rows: importRows, dry_run: false } });
    if (operation.ok) {
      void query.retry();
      toast.success("Participants imported.");
    }
    return operation;
  }

  const cohortOptions = useMemo(() => [{ id: cohortId, label: cohort.name }], [cohortId, cohort.name]);

  return (
    <CohortTabShell
      title="Participants"
      subtitle="Enrolled learners in this cohort."
      posture={query.posture}
      gate={query.gate}
      reference={query.reference}
      onRetry={query.retry}
      emptyTitle="No participants yet"
      emptyMessage="Participants added to this cohort will appear here."
      headerActions={
        (writeAllowed || importAllowed) && !cutoffClosed ? (
          <div className="flex items-center gap-2" data-testid="cohort-participant-actions">
            {canAdd && (
              <Button variant="secondary" size="sm" onClick={() => setAddOpen(true)} data-testid="cohort-add-participant">
                Add participant
              </Button>
            )}
            {canImport && (
              <Button variant="primary" size="sm" onClick={() => setImportOpen(true)} data-testid="cohort-import-participants">
                Import CSV
              </Button>
            )}
          </div>
        ) : (writeAllowed || importAllowed) && cutoffClosed ? (
          <span data-testid="cohort-cutoff-badge">
            <Badge variant="warning">Add window closed (week {cutoff.currentWeek})</Badge>
          </span>
        ) : undefined
      }
      overlays={
        <>
          {writeAllowed && (
            <ParticipantFormModal
              open={addOpen}
              mode="create"
              cohortOptions={cohortOptions}
              defaultCohortId={cohortId}
              onClose={() => {
                setAddOpen(false);
                create.reset();
              }}
              onSubmit={submitAdd}
              submitting={create.isPending}
              submitError={create.error}
            />
          )}
          {importAllowed && !cutoffClosed && (
            <BulkImportModal
              open={importOpen}
              cohortLabel={cohort.name}
              existingEmails={existingEmails}
              cutoff={cutoff}
              onClose={() => setImportOpen(false)}
              onSubmit={submitImport}
              submitting={lifecycle.bulkImport.isPending}
            />
          )}
        </>
      }
    >
      {cutoffClosed && (writeAllowed || importAllowed) && (
        <Alert variant="warning" data-testid="cohort-cutoff-notice">
          {cutoff.reason}
        </Alert>
      )}

      <CollectionToolbar
        collection={collection}
        searchPlaceholder="Filter participants"
        className="rounded-xl border border-line"
        stableChipNames
      />
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2
          collection={collection}
          caption="Cohort participants"
          emptyTitle="No participants match your search"
          emptyMessage="Clear or change the filters above to widen the view."
        />
        {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
      </div>
    </CohortTabShell>
  );
}